/**
 * Labels management page
 * Full CRUD interface for managing labels
 */
import { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import CustomFormDialog from '../../components/CustomFormDialog/CustomFormDialog';
import CustomPopup from '../../components/CustomPopup/CustomPopup';
import Table, { Column } from '../../components/Table/Table';
import { useAlert } from '../../contexts/AlertContext';
import { useTranslation } from 'react-i18next';
import { labelsApi, Label } from '../../services/labelsApi';
import { useServerSidePagination } from '../../hooks/useServerSidePagination';
import { PaginatedResponse } from '../../types/api';

interface LabelFormData {
  name: string;
  color: string;
  description: string;
  icon: string;
  category: string;
}

const Labels = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);

  // Wrapper function to convert 0-indexed page to 1-indexed for API
  const fetchLabels = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<Label>> => {
      const response = await labelsApi.getLabels({
        page: page + 1, // Convert 0-indexed to 1-indexed for API
        page_size: pageSize,
        search: search || undefined,
      });
      return response;
    },
    []
  );

  const pagination = useServerSidePagination(fetchLabels, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("labels.messages.fetchError");
      showAlert("error", `${t("labels.messages.fetchError")}: ${errorMessage}`, 5000);
      console.error("Błąd podczas pobierania etykiet:", error);
    },
  });

  // Create label mutation
  const createMutation = useMutation({
    mutationFn: async (data: LabelFormData) => {
      return labelsApi.createLabel({
        name: data.name,
        color: data.color || '#1976d2',
        description: data.description || '',
        icon: data.icon || '',
        category: data.category || '',
      });
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', t('labels.messages.created'));
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || t('labels.messages.createError');
      showAlert('error', message);
    },
  });

  // Update label mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: LabelFormData }) => {
      return labelsApi.updateLabel(id, {
        name: data.name,
        color: data.color,
        description: data.description,
        icon: data.icon,
        category: data.category,
      });
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', t('labels.messages.updated'));
      setIsDialogOpen(false);
      setEditingLabel(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || t('labels.messages.updateError');
      showAlert('error', message);
    },
  });

  // Delete label mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await labelsApi.deleteLabel(id);
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', t('labels.messages.deleted'));
      setDeleteDialogOpen(false);
      setSelectedLabel(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || t('labels.messages.deleteError');
      showAlert('error', message);
    },
  });

  const columns: Column<Label>[] = [
    {
      id: 'name',
      label: t('labels.columns.name'),
      flex: 1.5,
    },
    {
      id: 'color',
      label: t('labels.columns.color'),
      flex: 1,
      format: (value: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: value || '#1976d2',
              borderRadius: 1,
              border: '1px solid #ccc',
            }}
          />
          <span>{value || '#1976d2'}</span>
        </Box>
      ),
    },
    {
      id: 'category',
      label: t('labels.columns.category'),
      flex: 1,
      format: (value: string) => value || '-',
    },
    {
      id: 'icon',
      label: t('labels.columns.icon'),
      flex: 0.8,
      format: (value: string) => value || '-',
    },
    {
      id: 'products_count',
      label: t('labels.columns.products'),
      flex: 0.8,
      format: (value: number) => value || 0,
    },
    {
      id: 'documents_count',
      label: t('labels.columns.documents'),
      flex: 0.8,
      format: (value: number) => value || 0,
    },
    {
      id: 'id',
      label: t('common.actions'),
      flex: 1.2,
      align: 'center',
      sortable: false,
      format: (_: any, row: Label) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton
            size="small"
            color="primary"
            title={t('common.edit')}
            onClick={(e) => {
              e.stopPropagation();
              setEditingLabel(row);
              setIsDialogOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Tooltip
            title={
              (row.products_count || 0) > 0 || (row.documents_count || 0) > 0
                ? t('labels.messages.cannotDeleteInUse')
                : t('common.delete')
            }
            placement="top"
            arrow
          >
            <span style={{ display: 'inline-block' }}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!((row.products_count || 0) > 0 || (row.documents_count || 0) > 0)) {
                    setSelectedLabel(row);
                    setDeleteDialogOpen(true);
                  }
                }}
                disabled={(row.products_count || 0) > 0 || (row.documents_count || 0) > 0}
                sx={{
                  '&.Mui-disabled': {
                    color: 'rgba(150, 150, 150, 0.5)',
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(150, 150, 150, 0.5)',
                    },
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const labelFormFields = [
    { name: 'name', label: t('labels.form.name'), required: true },
    { name: 'color', label: t('labels.form.color'), required: false },
    { name: 'description', label: t('labels.form.description'), multiline: true, rows: 3, required: false },
    { name: 'icon', label: t('labels.form.icon'), required: false },
    {
      name: 'category',
      inputName: 'warehouse_label_category',
      label: t('labels.form.category'),
      required: false,
      autoComplete: 'off',
    },
  ];

  const validateLabel = (data: LabelFormData) => {
    const errors: Record<string, string> = {};
    if (!data.name) {
      errors.name = t('labels.validation.nameRequired');
    }
    if (data.color && !data.color.match(/^#[0-9A-Fa-f]{6}$/)) {
      errors.color = t('labels.validation.invalidColorFormat');
    }
    return errors;
  };

  const handleSaveLabel = (data: LabelFormData) => {
    if (editingLabel) {
      updateMutation.mutate({ id: editingLabel.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingLabel(null);
  };

  return (
    <Box>
      <Table
        columns={columns}
        data={pagination.data}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalRows={pagination.totalCount}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handlePageSizeChange}
        onSearchChange={pagination.handleSearchChange}
        rowKey="id"
        checkboxSelection={false}
        searchable={true}
        searchPlaceholder={t('labels.searchPlaceholder')}
        title={t('labels.title')}
        description={t('labels.description')}
        onAddButtonClick={() => setIsDialogOpen(true)}
        addButtonText={t('labels.addLabel')}
        loading={pagination.isLoading}
      />

      <CustomFormDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSaveLabel}
        title={editingLabel ? t('labels.editLabel') : t('labels.addNewLabel')}
        submitButtonText={editingLabel ? t('common.saveChanges') : t('common.add')}
        cancelButtonText={t('common.cancel')}
        formFields={labelFormFields}
        initialData={
          editingLabel
            ? {
              name: editingLabel.name,
              color: editingLabel.color,
              description: editingLabel.description || '',
              icon: editingLabel.icon || '',
              category: editingLabel.category || '',
            }
            : {
              name: '',
              color: '#1976d2',
              description: '',
              icon: '',
              category: '',
            }
        }
        validate={validateLabel}
        maxWidth="sm"
      />

      <CustomPopup
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t('labels.deleteConfirmation')}
        text={
          selectedLabel
            ? t('labels.deleteWarning', { name: selectedLabel.name })
            : ''
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={async () => {
          if (selectedLabel) {
            deleteMutation.mutate(selectedLabel.id);
          }
        }}
        confirmButtonColor="error"
      />
    </Box>
  );
};

export default Labels;
